import React, { useState } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Calendar, Download, Search, FileSearch } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

/**
 * iter-221i — Admin Price Reconciliation Tool
 * ─────────────────────────────────────────────
 * Bulk "what was the price on date X" lookup. Used at month-end to
 * reconcile vendor commission payouts against the historic price-of-record
 * for orders placed across the billing window.
 *
 * Flow:
 *   1. Admin pastes/imports a list of listing_ids (one per line, or
 *      comma-separated).
 *   2. Picks an `at_date` (defaults to "today").
 *   3. Hits "Lookup" — backend returns the base/customer/MRP triplet
 *      that was effective on that date for each id.
 *   4. Optionally export results to CSV for the finance team.
 */
const fmtINR = (v) =>
  v === null || v === undefined
    ? '—'
    : new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 2,
      }).format(Number(v));

const AdminPriceReconciliation = () => {
  const { toast } = useToast();
  const [listingIdsRaw, setListingIdsRaw] = useState('');
  const [atDate, setAtDate] = useState(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10); // YYYY-MM-DD
  });
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);

  const parseIds = (raw) =>
    raw
      .split(/[\n,]/g)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

  const runLookup = async () => {
    const ids = parseIds(listingIdsRaw);
    if (!ids.length) {
      toast({
        title: 'No listing IDs',
        description: 'Paste one or more listing IDs (newline or comma separated).',
        variant: 'destructive',
      });
      return;
    }
    if (ids.length > 1000) {
      toast({
        title: 'Too many IDs',
        description: 'Max 1000 listing IDs per request.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = sessionStorage.getItem('token') || localStorage.getItem('token');
      const isoDate = new Date(`${atDate}T23:59:59Z`).toISOString();
      const res = await fetch(
        `${BACKEND_URL}/api/listings/admin/price-at-date`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ listing_ids: ids, at_date: isoDate }),
        }
      );
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`HTTP ${res.status}: ${txt.slice(0, 120)}`);
      }
      const json = await res.json();
      setResults(json.results || []);
      toast({
        title: 'Lookup complete',
        description: `${json.results?.length || 0} rows · as of ${atDate}`,
      });
    } catch (e) {
      toast({
        title: 'Lookup failed',
        description: String(e.message || e),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = () => {
    if (!results.length) return;
    const header = [
      'listing_id',
      'title',
      'vendor_id',
      'as_of',
      'base_price',
      'aniyor_listing_price',
      'mrp_price',
      'effective_from',
      'source',
    ];
    const rows = results.map((r) =>
      header
        .map((k) => {
          const v = r[k];
          if (v === null || v === undefined) return '';
          const s = String(v).replace(/"/g, '""');
          return /[,"\n]/.test(s) ? `"${s}"` : s;
        })
        .join(',')
    );
    const csv = [header.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `price-reconciliation-${atDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <DashboardLayout>
      <div className="p-6 max-w-6xl mx-auto" data-testid="admin-price-reconciliation">
        <div className="flex items-center gap-3 mb-6">
          <FileSearch className="w-6 h-6 text-gray-700" />
          <h1 className="text-2xl font-semibold">Price reconciliation</h1>
          <Badge variant="secondary">Last 90 days</Badge>
        </div>

        <p className="text-sm text-gray-600 mb-6 max-w-3xl">
          Look up what each listing was priced at <em>as of</em> a specific date.
          Results pull from the rolling 90-day price-history ledger; if a
          listing has no recorded change before the chosen date, the live
          price is returned with <code className="px-1 bg-gray-100 rounded">source = live_listing</code>.
        </p>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">Inputs</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="listing-ids" className="mb-1 block">
                Listing IDs (newline or comma separated, max 1000)
              </Label>
              <Textarea
                id="listing-ids"
                value={listingIdsRaw}
                onChange={(e) => setListingIdsRaw(e.target.value)}
                rows={6}
                placeholder={'lst_abc123\nlst_def456\nlst_ghi789'}
                className="font-mono text-xs"
                data-testid="reconcile-listing-ids-input"
              />
              <p className="mt-1 text-xs text-gray-500">
                {parseIds(listingIdsRaw).length} ID
                {parseIds(listingIdsRaw).length !== 1 ? 's' : ''} parsed
              </p>
            </div>

            <div className="flex items-end gap-3">
              <div className="flex-1 max-w-xs">
                <Label htmlFor="at-date" className="mb-1 block">
                  As of date
                </Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="at-date"
                    type="date"
                    value={atDate}
                    onChange={(e) => setAtDate(e.target.value)}
                    className="pl-9"
                    data-testid="reconcile-at-date-input"
                  />
                </div>
              </div>
              <Button
                onClick={runLookup}
                disabled={loading}
                data-testid="reconcile-lookup-btn"
              >
                <Search className="w-4 h-4 mr-2" />
                {loading ? 'Looking up…' : 'Lookup'}
              </Button>
              {results.length > 0 && (
                <Button
                  variant="outline"
                  onClick={exportCsv}
                  data-testid="reconcile-export-csv-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {results.length > 0 && (
          <Card data-testid="reconcile-results-card">
            <CardHeader>
              <CardTitle className="text-base">
                Results · {results.length} rows · as of {atDate}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b">
                      <th className="py-2 pr-3 font-medium text-gray-600">Listing</th>
                      <th className="py-2 pr-3 font-medium text-gray-600 text-right">Base</th>
                      <th className="py-2 pr-3 font-medium text-gray-600 text-right">Customer</th>
                      <th className="py-2 pr-3 font-medium text-gray-600 text-right">MRP</th>
                      <th className="py-2 pr-3 font-medium text-gray-600">Source</th>
                      <th className="py-2 font-medium text-gray-600">Effective from</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((r, idx) => (
                      <tr
                        key={`${r.listing_id}-${idx}`}
                        className="border-b last:border-0"
                        data-testid={`reconcile-row-${r.listing_id}`}
                      >
                        <td className="py-2 pr-3">
                          <div className="font-mono text-xs text-gray-500">{r.listing_id}</div>
                          <div className="text-gray-800">{r.title || '—'}</div>
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums">
                          {fmtINR(r.base_price)}
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums">
                          {fmtINR(r.aniyor_listing_price)}
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums">
                          {fmtINR(r.mrp_price)}
                        </td>
                        <td className="py-2 pr-3">
                          <Badge
                            variant={r.source === 'live_listing' ? 'outline' : 'secondary'}
                            className="text-xs"
                          >
                            {r.source || (r.found ? '—' : 'not_found')}
                          </Badge>
                        </td>
                        <td className="py-2 text-xs text-gray-600 whitespace-nowrap">
                          {r.effective_from
                            ? new Date(r.effective_from).toLocaleString('en-IN', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                              })
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminPriceReconciliation;
